import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	checkProjectCode,
	createProject as createProjectApi,
	editProject,
	removeProjects,
} from '@api/projectManagement';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { IResponseError } from '@interfaces/errors/apiErrors';
import {
	ICheckProjectCodeDTO,
	ICreateProjectDTO,
	IDeleteProjectsDTO,
	IEditProjectDTO,
} from '@interfaces/portal/dto/projectManagementDTO';

import { EMenuActionsTypes, ESnackbarTypes } from '@enums/common';
import { EErrorCodes } from '@enums/errorCodes';

import {
	CHECK_PROJECT_CODE,
	CREATE_PROJECT,
	DELETE_PROJECTS,
	GET_PROJECTS_LIST,
	UPDATE_PROJECT,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

export interface IMutateProjectManagementReturned {
	createProject: (data: ICreateProjectDTO) => void;
	isCreatingProject: boolean;
	updateProject: (data: IEditProjectDTO) => void;
	isUpdatingProject: boolean;
	handleDeleteProjects: (data: IGroupOperationWithActionDTO) => void;
	handleDeleteProject: (id: string) => void;
	isLoadingDelete: boolean;
	isLoadingCheckProjectCode: boolean;
	handleCheckProjectCode: (data: ICheckProjectCodeDTO) => void;
}

interface IUseMutateProjectManagement {
	organizationId?: string | null;
	searchValue?: string;
	status?: string | null;
	closeDeleteProjectsConfirm?: () => void;
}

export function useMutatePlatformOwnerProjects({
	organizationId,
	searchValue,
	status,
	closeDeleteProjectsConfirm,
}: IUseMutateProjectManagement): IMutateProjectManagementReturned {
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();

	const {
		mutateAsync: createProject,
		isLoading: isCreatingProject,
	} = useMutation<ICreateProjectDTO>({
		mutationFn: createProjectApi,
		mutationKey: [CREATE_PROJECT],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.createProjectSuccess,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_PROJECTS_LIST]);
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: updateProject,
		isLoading: isUpdatingProject,
	} = useMutation<IEditProjectDTO>({
		mutationFn: editProject,
		mutationKey: [UPDATE_PROJECT],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updateProjectSuccess,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([GET_PROJECTS_LIST]);
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutate: handleDeleteProjects,
		isLoading: isLoadingDeleteProjects,
	} = useMutation<IGroupOperationWithActionDTO>({
		mutationKey: [
			DELETE_PROJECTS,
			searchValue,
			organizationId,
			status,
		],
		mutationFn: ({ ids, actionType }: IGroupOperationWithActionDTO) => {
			const filter: IDeleteProjectsDTO = {
				searchValue,
				organizationId,
				status,
			};

			return removeProjects({
				filter: actionType === EMenuActionsTypes.allFiltered
					? filter
					: organizationId
						? { organizationId }
						: null,
				ids,
				isAll: actionType === EMenuActionsTypes.all || actionType === EMenuActionsTypes.allFiltered,
			});
		},
		onSuccess: () => {
			if (closeDeleteProjectsConfirm) {
				closeDeleteProjectsConfirm();
			}

			dispatch(setSnackbar({
				message: responseMessages.removedProjects,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([GET_PROJECTS_LIST]),
			];
		},
		onError: (err: IResponseError) => {
			if (closeDeleteProjectsConfirm) {
				closeDeleteProjectsConfirm();
			}

			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: handleDeleteProject,
		isLoading: isLoadingDeleteProject,
	} = useMutation<string>({
		mutationKey: [DELETE_PROJECTS],
		mutationFn: (id: string) => removeProjects({
			ids: [id],
			isAll: false,
			filter: null,
		}),
		onSuccess: () => {
			if (closeDeleteProjectsConfirm) {
				closeDeleteProjectsConfirm();
			}

			dispatch(setSnackbar({
				message: responseMessages.removedProject,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([GET_PROJECTS_LIST]),
			];
		},
		onError: (err: IResponseError) => {
			if (closeDeleteProjectsConfirm) {
				closeDeleteProjectsConfirm();
			}

			handleErrorFromMessage(err);
		},
	});

	const {
		mutateAsync: handleCheckProjectCode,
		isLoading: isLoadingCheckProjectCode,
	} = useMutation<ICheckProjectCodeDTO>({
		mutationKey: [CHECK_PROJECT_CODE],
		mutationFn: checkProjectCode,
		onError: (err: IResponseError) => {
			if (!err.errors.find((item) => (
				item.code === EErrorCodes.projectCodeMustBeUnique
				|| item.code === EErrorCodes.projectCodeMustStartWithUppercase
				|| item.code === EErrorCodes.projectCodeMustHaveAtList2Characters
			))) {
				handleErrorFromMessage(err);
			}
		},
	});

	return {
		createProject,
		isCreatingProject,
		updateProject,
		isUpdatingProject,
		handleDeleteProject,
		handleDeleteProjects,
		isLoadingDelete: isLoadingDeleteProject || isLoadingDeleteProjects,
		handleCheckProjectCode,
		isLoadingCheckProjectCode,
	};
}
